import React, { useState, useEffect } from 'react';
import { FoxSpinnerInline } from '../ui/FoxSpinner';
import api from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { UserX, Search, X } from 'lucide-react';

const EventExclusionsManager = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [updating, setUpdating] = useState({});
  
  // Estado do filtro (dropdown único)
  const [filterStatus, setFilterStatus] = useState('todos');

  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/agency/exclusions', { timeout: 60000 });
      setMembers(response.data.members || []);
      console.log(`✅ Carregados ${response.data.total} membros da agência`);
    } catch (error) {
      console.error('Erro ao carregar membros:', error);
      const message = error.response?.data?.detail || error.message || 'Erro ao carregar lista de membros';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleExclusion = async (showId, event, value) => {
    const key = `${showId}-${event}`;
    setUpdating(prev => ({ ...prev, [key]: true }));

    try {
      // Buscar estado atual
      const current = members.find(m => m.show_id === showId);
      if (!current) return;

      const updates = {
        trabalhou_ganhou: current.excluded_from.trabalhou_ganhou || false,
        ranking_estrelas: current.excluded_from.ranking_estrelas || false
      };

      // Atualizar o campo específico
      if (event === 'trabalhou_ganhou') {
        updates.trabalhou_ganhou = value;
      } else if (event === 'ranking_estrelas') {
        updates.ranking_estrelas = value;
      }

      await api.put(`/agency/exclusions/${showId}`, updates);
      
      toast.success(value ? `Host excluído do evento` : `Host reativado no evento`);
      loadMembers();
    } catch (error) {
      console.error('Erro ao atualizar exclusão:', error);
      toast.error('Erro ao atualizar exclusão');
    } finally {
      setUpdating(prev => ({ ...prev, [key]: false }));
    }
  };

  // Limpar todos os filtros
  const clearFilters = () => {
    setSearchTerm('');
    setFilterStatus('todos');
  };

  // Verificar se algum filtro está ativo
  const hasActiveFilters = () => {
    return searchTerm !== '' || filterStatus !== 'todos';
  };

  // Filtrar membros
  const filteredMembers = members.filter(m => {
    // Filtro de busca
    const matchesSearch = !searchTerm || 
      m.show_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.nickname.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;

    // Filtro de status
    const trabalhouExcluded = m.excluded_from.trabalhou_ganhou;
    const rankingExcluded = m.excluded_from.ranking_estrelas;
    
    switch(filterStatus) {
      case 'todos':
        return true;
      case 'excluidos_trabalhou':
        return trabalhouExcluded;
      case 'excluidos_ranking':
        return rankingExcluded;
      case 'excluidos_ambos':
        return trabalhouExcluded && rankingExcluded;
      case 'nenhum_excluido':
        return !trabalhouExcluded && !rankingExcluded;
      default:
        return true;
    }
  });

  // Estatísticas
  const stats = {
    total: members.length,
    trabalhouGanhou: members.filter(m => m.excluded_from.trabalhou_ganhou).length,
    rankingEstrelas: members.filter(m => m.excluded_from.ranking_estrelas).length,
    ambos: members.filter(m => m.excluded_from.trabalhou_ganhou && m.excluded_from.ranking_estrelas).length
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <FoxSpinnerInline size="lg" />
          <p className="mt-4 text-gray-500">Carregando membros da agência...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Estatísticas */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-3 sm:p-4 text-center">
            <div className="text-xl sm:text-2xl font-bold text-gray-900">{stats.total}</div>
            <div className="text-xs text-gray-600">Total Membros</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 sm:p-4 text-center">
            <div className="text-xl sm:text-2xl font-bold text-purple-600">{stats.trabalhouGanhou}</div>
            <div className="text-xs text-gray-600">Excl. Trabalhou</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 sm:p-4 text-center">
            <div className="text-xl sm:text-2xl font-bold text-yellow-600">{stats.rankingEstrelas}</div>
            <div className="text-xs text-gray-600">Excl. Ranking</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 sm:p-4 text-center">
            <div className="text-xl sm:text-2xl font-bold text-red-600">{stats.ambos}</div>
            <div className="text-xs text-gray-600">Excl. Ambos</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabela de Membros */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <CardTitle className="text-base sm:text-lg flex items-center gap-2">
              <UserX className="w-5 h-5" />
              Lista de Membros ({filteredMembers.length})
            </CardTitle>
          </div>
        </CardHeader>
        
        {/* Barra de Filtros Simples */}
        <CardContent className="border-b pb-4">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Campo de Busca */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="🔍 Buscar por ID ou nome..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 text-sm"
              />
            </div>

            {/* Dropdown de Filtro */}
            <div className="flex gap-2">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white cursor-pointer hover:border-gray-400 transition-colors"
              >
                <option value="todos">📋 Mostrar Todos</option>
                <option value="excluidos_trabalhou">💼 Excluídos: Trabalhou Ganhou</option>
                <option value="excluidos_ranking">⭐ Excluídos: Ranking de Estrelas</option>
                <option value="excluidos_ambos">🚫 Excluídos: Ambos Eventos</option>
                <option value="nenhum_excluido">✅ Nenhum Excluído</option>
              </select>

              {/* Botão Limpar Filtros */}
              {hasActiveFilters() && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearFilters}
                  className="text-red-600 border-red-300 hover:bg-red-50 whitespace-nowrap"
                >
                  <X className="w-4 h-4 mr-1" />
                  Limpar
                </Button>
              )}
            </div>
          </div>
        </CardContent>

        <CardContent>
          {filteredMembers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <UserX className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">
                {searchTerm ? 'Nenhum membro encontrado' : 'Nenhum membro na agência'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2 sm:p-3 font-semibold text-gray-700">Kako ID</th>
                    <th className="text-left p-2 sm:p-3 font-semibold text-gray-700 hidden sm:table-cell">Nome</th>
                    <th className="text-center p-2 sm:p-3 font-semibold text-purple-700">
                      <div className="flex flex-col items-center">
                        <span>💼</span>
                        <span className="text-xs">Trabalhou</span>
                      </div>
                    </th>
                    <th className="text-center p-2 sm:p-3 font-semibold text-yellow-700">
                      <div className="flex flex-col items-center">
                        <span>⭐</span>
                        <span className="text-xs">Ranking</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMembers.map((member) => (
                    <tr key={member.show_id} className="border-b hover:bg-gray-50">
                      <td className="p-2 sm:p-3">
                        <div className="font-mono text-xs sm:text-sm font-semibold">{member.show_id}</div>
                        <div className="text-xs text-gray-500 sm:hidden">{member.nickname}</div>
                      </td>
                      <td className="p-2 sm:p-3 hidden sm:table-cell">
                        <div className="flex items-center gap-2">
                          {member.avatar && (
                            <img src={member.avatar} alt={member.nickname} className="w-8 h-8 rounded-full" />
                          )}
                          <div className="text-xs sm:text-sm">{member.nickname}</div>
                        </div>
                      </td>
                      <td className="p-2 sm:p-3 text-center">
                        <div className="flex justify-center">
                          <Switch
                            checked={member.excluded_from.trabalhou_ganhou || false}
                            onCheckedChange={(value) => handleToggleExclusion(member.show_id, 'trabalhou_ganhou', value)}
                            disabled={updating[`${member.show_id}-trabalhou_ganhou`]}
                            className="data-[state=checked]:bg-red-600"
                          />
                        </div>
                        {member.excluded_from.trabalhou_ganhou && (
                          <Badge className="bg-red-600 text-[10px] mt-1">Excluído</Badge>
                        )}
                      </td>
                      <td className="p-2 sm:p-3 text-center">
                        <div className="flex justify-center">
                          <Switch
                            checked={member.excluded_from.ranking_estrelas || false}
                            onCheckedChange={(value) => handleToggleExclusion(member.show_id, 'ranking_estrelas', value)}
                            disabled={updating[`${member.show_id}-ranking_estrelas`]}
                            className="data-[state=checked]:bg-red-600"
                          />
                        </div>
                        {member.excluded_from.ranking_estrelas && (
                          <Badge className="bg-red-600 text-[10px] mt-1">Excluído</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Legenda */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-3 sm:p-4">
          <div className="text-xs sm:text-sm space-y-2">
            <p className="font-semibold text-blue-900">📌 Como funciona:</p>
            <ul className="list-disc list-inside text-blue-800 space-y-1 ml-2">
              <li><strong>Switch LIGADO</strong> (vermelho) = Host EXCLUÍDO do evento</li>
              <li><strong>Switch DESLIGADO</strong> (cinza) = Host ATIVO no evento</li>
              <li>Use a <strong>busca</strong> para filtrar por nome ou ID</li>
              <li>Use o <strong>dropdown</strong> para filtrar por categoria de exclusão</li>
              <li>Mudanças são salvas automaticamente</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EventExclusionsManager;
